import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useStores } from "@/hooks/useStores";
import type { TimelineDateMarker } from "@/domains/documents/types/timeline";
import { ZOOM_CONFIG } from "@/domains/documents/types/timeline";
import type {
  ServerTimelineData,
  ServerTimelineCluster,
} from "@/domains/documents/utils/timelineClustering";
import {
  bucketsToTimelineClusters,
  generateServerDateMarkers,
} from "@/domains/documents/utils/timelineClustering";
import type {
  ExpandedClusterState,
  DocumentRowMapped,
  TimelineFilterParams,
} from "@/stores/documents/repository";

/**
 * Threshold for max zoom mode where we show individual documents.
 * At or above this zoom level, we switch from clusters to individual docs.
 */
const MAX_ZOOM_INDIVIDUAL_THRESHOLD = 90;

interface UseServerTimelineZoomOptions {
  /** Building ID for data fetching */
  buildingId: string;
  /** Initial zoom level (0-100) */
  initialZoomLevel?: number;
  /** Container height in pixels */
  containerHeight: number;
  /** Callback when zoom changes */
  onZoomChange?: (level: number) => void;
  /** Filters to apply to timeline data (project, categories, etc.) */
  filters?: TimelineFilterParams;
  /**
   * Called once both halves of a filters change have settled — the bucket
   * summary AND the content-match search. Lets the page-level search box, which
   * lives outside the timeline and has no fetch to watch, hold its spinner
   * until the rows for the committed query have actually landed.
   *
   * Both halves, not just the summary: content-only matches are search results
   * too, and they arrive on the slower leg.
   */
  onResolved?: () => void;
}

interface UseServerTimelineZoomReturn {
  /** Current zoom level (0-100) */
  zoomLevel: number;
  /** Set zoom level directly */
  setZoomLevel: (level: number) => void;
  /** Zoom in one step */
  zoomIn: () => void;
  /** Zoom out one step */
  zoomOut: () => void;
  /** Clustered timeline data from server buckets */
  timelineData: ServerTimelineData;
  /** Date markers for the stem */
  dateMarkers: TimelineDateMarker[];
  /** Handle scroll wheel zoom */
  handleWheelZoom: (event: WheelEvent) => void;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Total documents in timeline */
  totalDocuments: number;
  /** Current granularity (year/month/week/day) */
  granularity: string;
  // Cluster expansion
  /** Expand a cluster to fetch its documents */
  expandCluster: (cluster: ServerTimelineCluster) => void;
  /** Collapse an expanded cluster */
  collapseCluster: (cluster: ServerTimelineCluster) => void;
  /** Load more documents in an expanded cluster */
  loadMoreInCluster: (cluster: ServerTimelineCluster) => void;
  /** Get expanded state for a cluster (if expanded) */
  getExpandedCluster: (cluster: ServerTimelineCluster) => ExpandedClusterState | undefined;
  /** Check if a cluster is expanded */
  isClusterExpanded: (cluster: ServerTimelineCluster) => boolean;
  // Individual docs mode (max zoom)
  /** Whether we're in max zoom (individual docs) mode */
  isMaxZoomMode: boolean;
  /** Individual documents for max zoom mode */
  individualDocs: DocumentRowMapped[];
  /** Whether there are more individual docs to load */
  hasMoreIndividualDocs: boolean;
  /**
   * True when paging the individual docs gave up short of the end rather than
   * reaching it. Kept apart from `hasMoreIndividualDocs` on purpose: that one
   * drives the scroll-triggered auto-load, and a re-read that keeps dead-ending
   * would refetch on every scroll event. This one drives a button.
   */
  individualDocsTruncated: boolean;
  /** Whether individual docs are loading */
  isLoadingIndividualDocs: boolean;
  /** Load more individual docs (for infinite scroll) */
  loadMoreIndividualDocs: () => void;
}

/**
 * useServerTimelineZoom - Hook for server-side timeline aggregation
 *
 * Uses DocumentStore's timeline actions for efficient handling of 6000+ documents.
 * Only fetches bucketed counts on initial load, then lazy loads documents
 * when clusters are expanded.
 *
 * At max zoom (>= 90), switches to individual documents mode where each document
 * is rendered separately with infinite scroll pagination.
 *
 * @example
 * ```tsx
 * const {
 *   timelineData,
 *   zoomLevel,
 *   isMaxZoomMode,
 *   individualDocs,
 *   loadMoreIndividualDocs,
 * } = useServerTimelineZoom({
 *   buildingId: "123",
 *   containerHeight: 600,
 * });
 * ```
 */
export function useServerTimelineZoom(
  options: UseServerTimelineZoomOptions
): UseServerTimelineZoomReturn {
  const {
    buildingId,
    initialZoomLevel = ZOOM_CONFIG.DEFAULT,
    containerHeight,
    onZoomChange,
    filters,
    onResolved,
  } = options;

  const { documentStore } = useStores();
  const [zoomLevel, setZoomLevelState] = useState<number>(initialZoomLevel);
  const prevMaxZoomModeRef = useRef<boolean>(false);

  // Determine if we're in max zoom (individual docs) mode
  const isMaxZoomMode = zoomLevel >= MAX_ZOOM_INDIVIDUAL_THRESHOLD;

  // Load timeline summary on mount and when filters change (COUNTS ONLY - no documents!)
  // Also fetch content-match docs in parallel so the merge layer can surface docs
  // whose body text matches the search term but whose filename doesn't.
  // Read through a ref so a caller passing an inline arrow doesn't re-run the
  // fetch effect (and clear the timeline cache) on every one of its renders.
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;

  useEffect(() => {
    let cancelled = false;

    if (buildingId) {
      void Promise.allSettled([
        documentStore.loadTimelineSummary(buildingId, undefined, filters),
        // Content matches are held to the same filters as the rest of the
        // timeline (filters.projectIds carries the scope's project when one is set).
        documentStore.loadTimelineContentMatches(buildingId, filters?.searchText, filters),
      ]).then(() => {
        if (!cancelled) onResolvedRef.current?.();
      });
    }

    return () => {
      cancelled = true;
      // Cleanup on unmount
      documentStore.clearTimelineCache();
    };
  }, [buildingId, documentStore, filters]);

  // Realtime resyncs land in the store, which has no idea what the timeline is
  // currently showing — the filter set and zoom mode live here. So the store
  // holds a callback rather than a copy of that state, and this is where it is
  // supplied and withdrawn.
  useEffect(() => {
    if (!buildingId) return;
    documentStore.setTimelineRevalidate(() => {
      void documentStore.loadTimelineSummary(buildingId, undefined, filters);
      void documentStore.loadTimelineContentMatches(buildingId, filters?.searchText, filters);
      // Only fetched while the user is zoomed all the way in.
      if (prevMaxZoomModeRef.current) {
        void documentStore.loadTimelineIndividualDocs(buildingId, true, filters);
      }
    });
    return () => documentStore.setTimelineRevalidate(null);
  }, [buildingId, documentStore, filters]);

  // Track previous filters to detect changes
  const prevFiltersRef = useRef<TimelineFilterParams | undefined>(filters);

  // Handle switching to/from max zoom mode AND filter changes while in max zoom
  useEffect(() => {
    const wasMaxZoom = prevMaxZoomModeRef.current;
    const prevFilters = prevFiltersRef.current;
    prevMaxZoomModeRef.current = isMaxZoomMode;
    prevFiltersRef.current = filters;

    if (isMaxZoomMode && !wasMaxZoom) {
      // Entering max zoom mode - load individual docs with filters
      documentStore.loadTimelineIndividualDocs(buildingId, true, filters);
    } else if (isMaxZoomMode && wasMaxZoom && filters !== prevFilters) {
      // Already in max zoom mode but filters changed - reload with new filters
      documentStore.loadTimelineIndividualDocs(buildingId, true, filters);
    } else if (!isMaxZoomMode && wasMaxZoom) {
      // Exiting max zoom mode - clear individual docs
      documentStore.clearTimelineIndividualDocs();
    }
  }, [isMaxZoomMode, buildingId, documentStore, filters]);

  // Set zoom level with bounds checking
  const setZoomLevel = useCallback(
    (level: number) => {
      const clampedLevel = Math.max(ZOOM_CONFIG.MIN, Math.min(ZOOM_CONFIG.MAX, level));
      setZoomLevelState(clampedLevel);
      onZoomChange?.(clampedLevel);

      // Only update granularity if not in max zoom mode
      // (max zoom mode uses individual docs, not buckets)
      if (clampedLevel < MAX_ZOOM_INDIVIDUAL_THRESHOLD) {
        documentStore.updateTimelineGranularity(clampedLevel, buildingId, filters);
      }
    },
    [buildingId, documentStore, onZoomChange, filters]
  );

  // Zoom in by step
  const zoomIn = useCallback(() => {
    setZoomLevel(zoomLevel + ZOOM_CONFIG.STEP);
  }, [zoomLevel, setZoomLevel]);

  // Zoom out by step
  const zoomOut = useCallback(() => {
    setZoomLevel(zoomLevel - ZOOM_CONFIG.STEP);
  }, [zoomLevel, setZoomLevel]);

  // Convert server-side buckets to renderable clusters
  const timelineData = useMemo((): ServerTimelineData => {
    const buckets = documentStore.timelineBuckets;
    if (buckets.length === 0) {
      return { clusters: [], totalDocuments: 0, timeRange: null };
    }
    return bucketsToTimelineClusters(buckets, zoomLevel, containerHeight);
  }, [documentStore.timelineBuckets, zoomLevel, containerHeight]);

  // Generate date markers from clusters
  const dateMarkers = useMemo(() => {
    return generateServerDateMarkers(timelineData.clusters, timelineData.timeRange, zoomLevel);
  }, [timelineData.clusters, timelineData.timeRange, zoomLevel]);

  // Handle scroll wheel zoom (Cmd/Ctrl + scroll or pinch)
  const handleWheelZoom = useCallback(
    (event: WheelEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;

      event.preventDefault();

      const delta = -event.deltaY * 0.1;
      const newZoom = Math.max(ZOOM_CONFIG.MIN, Math.min(ZOOM_CONFIG.MAX, zoomLevel + delta));

      setZoomLevelState(newZoom);
      onZoomChange?.(newZoom);

      // Update granularity if needed (only if not in max zoom mode)
      if (newZoom < MAX_ZOOM_INDIVIDUAL_THRESHOLD) {
        documentStore.updateTimelineGranularity(newZoom, buildingId, filters);
      }
    },
    [zoomLevel, buildingId, documentStore, onZoomChange, filters]
  );

  // Cluster expansion handlers
  const expandCluster = useCallback(
    (cluster: ServerTimelineCluster) => {
      documentStore.expandCluster(buildingId, cluster.startTime, cluster.endTime, filters);
    },
    [buildingId, documentStore, filters]
  );

  const collapseCluster = useCallback(
    (cluster: ServerTimelineCluster) => {
      documentStore.collapseCluster(cluster.startTime, cluster.endTime);
    },
    [documentStore]
  );

  const loadMoreInCluster = useCallback(
    (cluster: ServerTimelineCluster) => {
      documentStore.loadMoreInCluster(buildingId, cluster.startTime, cluster.endTime, filters);
    },
    [buildingId, documentStore, filters]
  );

  const getExpandedCluster = useCallback(
    (cluster: ServerTimelineCluster): ExpandedClusterState | undefined => {
      return documentStore.getExpandedCluster(cluster.startTime, cluster.endTime);
    },
    [documentStore]
  );

  const isClusterExpanded = useCallback(
    (cluster: ServerTimelineCluster): boolean => {
      return documentStore.isClusterExpanded(cluster.startTime, cluster.endTime);
    },
    [documentStore]
  );

  // Individual docs mode handlers
  const loadMoreIndividualDocs = useCallback(() => {
    // `truncated` counts as "more to load": the store's load-more re-reads a
    // list whose paging gave up, and only the button reaches this with it set —
    // the scroll handler upstream stays on `hasMore` alone.
    if (
      isMaxZoomMode &&
      (documentStore.timelineIndividualHasMore || documentStore.timelineIndividualTruncated) &&
      !documentStore.timelineIndividualLoadingMore
    ) {
      documentStore.loadTimelineIndividualDocs(buildingId, false, filters);
    }
  }, [buildingId, documentStore, isMaxZoomMode, filters]);

  return {
    zoomLevel,
    setZoomLevel,
    zoomIn,
    zoomOut,
    timelineData,
    dateMarkers,
    handleWheelZoom,
    isLoading: documentStore.timelineLoading || documentStore.timelineIndividualLoading,
    error: documentStore.timelineError,
    totalDocuments: documentStore.timelineTotalDocuments,
    granularity: documentStore.timelineGranularity,
    // Cluster expansion
    expandCluster,
    collapseCluster,
    loadMoreInCluster,
    getExpandedCluster,
    isClusterExpanded,
    // Individual docs mode
    isMaxZoomMode,
    individualDocs: documentStore.timelineIndividualDocs,
    hasMoreIndividualDocs: documentStore.timelineIndividualHasMore,
    individualDocsTruncated: documentStore.timelineIndividualTruncated,
    isLoadingIndividualDocs:
      documentStore.timelineIndividualLoading || documentStore.timelineIndividualLoadingMore,
    loadMoreIndividualDocs,
  };
}

export type { ExpandedClusterState, DocumentRowMapped };
